import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CustomerLink from "@/components/CustomerLink";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  PencilSimple,
  Printer,
  PaperPlaneTilt,
  ClockCounterClockwise,
  XCircle,
  Spinner,
  Copy,
  Eye,
  CheckCircle,
  WarningCircle,
  UserPlus,
  UserMinus,
  Truck,
  ArrowRight,
  Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtTimeOnly = (iso) =>
  new Date(iso).toLocaleTimeString("en-CA", {
    hour: "2-digit", minute: "2-digit", hour12: false,
  });

const LOCKED = new Set(["Delivered", "Completed"]);

export default function OrderViewDialog({ open, order, onClose, onChanged }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [timeline, setTimeline] = useState([]);
  const [loadingTimeline, setLoadingTimeline] = useState(false);
  const [resending, setResending] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    setTimelineOpen(false);
    setTimeline([]);
  }, [order?.id]);

  const canEdit =
    user && ["Administrator", "Office Staff"].includes(user.role) && order &&
    !LOCKED.has(order.status);

  const loadTimeline = async () => {
    if (!order) return;
    setLoadingTimeline(true);
    try {
      const res = await api.get(`/orders/${order.id}/timeline`);
      setTimeline(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load timeline");
    } finally {
      setLoadingTimeline(false);
    }
  };

  const toggleTimeline = async () => {
    const next = !timelineOpen;
    setTimelineOpen(next);
    if (next && timeline.length === 0) await loadTimeline();
  };

  const handleEdit = () => {
    if (!order) return;
    onClose();
    navigate(`/new-order?edit=${order.id}`);
  };

  const handleDuplicate = () => {
    if (!order) return;
    onClose();
    navigate(`/new-order?duplicate=${order.id}`);
  };

  const handlePrint = () => {
    if (!order) return;
    const w = window.open("", "_blank", "width=720,height=900");
    if (!w) return;
    w.document.write(renderPrintHtml(order));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 250);
  };

  const handleResend = async () => {
    if (!order) return;
    setResending(true);
    try {
      const res = await api.post(`/orders/${order.id}/resend-notification`);
      if (res.data.notification_status === "Sent") {
        toast.success("Email notification delivered");
      } else {
        toast.error("Email send failed again");
      }
      onChanged?.(res.data);
      // refresh timeline if visible
      if (timelineOpen) await loadTimeline();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  const handleCancel = async () => {
    if (!order) return;
    if (!window.confirm(`Cancel order ${order.order_number}? Assigned drivers will be notified.`)) {
      return;
    }
    setCancelling(true);
    try {
      const res = await api.put(`/orders/${order.id}/status`, { status: "Cancelled" });
      toast.success(`Order ${order.order_number} cancelled`);
      onChanged?.(res.data);
      if (timelineOpen) await loadTimeline();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel order");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent
        data-testid="order-view-dialog"
        className="max-h-[92vh] overflow-y-auto sm:max-w-3xl"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <span className="font-mono font-semibold text-gray-900">
              {order?.order_number}
            </span>
            {order && (
              <>
                <StatusBadge status={order.status} />
                <NotificationBadge status={order.notification_status} />
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Dispatch Actions menu */}
        {order && (
          <div
            data-testid="dispatch-actions"
            className="-mt-1 flex flex-wrap gap-1.5 border-b border-gray-200 pb-3"
          >
            <Action
              testid="action-view"
              icon={Eye}
              label="View Order"
              onClick={() => {/* the dialog itself is the view */ }}
              active
            />
            <Action
              testid="action-edit"
              icon={PencilSimple}
              label="Edit Order"
              onClick={handleEdit}
              disabled={!canEdit}
              hint={canEdit ? "" : `Order is ${order.status}`}
            />
            <Action
              testid="action-duplicate"
              icon={Copy}
              label="Duplicate Order"
              onClick={handleDuplicate}
            />
            <Action
              testid="action-cancel"
              icon={XCircle}
              label="Cancel Order"
              onClick={handleCancel}
              loading={cancelling}
              disabled={!canEdit || order.status === "Cancelled"}
              tone="danger"
            />
            {order.notification_status === "Failed" && (
              <Action
                testid="action-resend"
                icon={PaperPlaneTilt}
                label="Retry Notification"
                onClick={handleResend}
                loading={resending}
                tone="primary"
              />
            )}
            <Action
              testid="action-timeline"
              icon={ClockCounterClockwise}
              label="View Dispatch Timeline"
              onClick={toggleTimeline}
              active={timelineOpen}
            />
            <Action
              testid="action-print"
              icon={Printer}
              label="Print Order"
              onClick={handlePrint}
            />
          </div>
        )}

        {!order ? (
          <div className="py-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="space-y-5 pt-2 text-[15px] text-gray-900">
            {timelineOpen && (
              <DispatchTimeline
                events={timeline}
                loading={loadingTimeline}
                order={order}
              />
            )}

            <Section title="Customer">
              <Row label="Customer Name" bold>
                <CustomerLink
                  customerId={order.customer_id}
                  customerName={order.customer_name}
                  testId={`orderview-customer-link-${order.order_number}`}
                />
              </Row>
              <Row label="Account Number" value={order.account_number} mono />
              <Row label="Mailing Address" value={order.mailing_address} />
              <Row label="Phone Number" value={order.phone_number} mono />
            </Section>

            <Section title="Delivery">
              <Row label="Requested" value={order.priority} />
              {order.alternate_delivery_location && (
                <Row label="Alternate Location" value={order.alternate_delivery_location} />
              )}
              {order.directions && <Row label="Directions" value={order.directions} />}
              {order.delivery_notes && <Row label="Delivery Notes" value={order.delivery_notes} />}
            </Section>

            <Section title="Products">
              <ul className="ml-5 list-disc text-gray-800">
                {order.products.map((p, i) => (
                  <li key={i}>{p.product_name}</li>
                ))}
              </ul>
            </Section>

            <Section title="Drivers Notified">
              {(order.driver_names || []).length === 0 ? (
                <div className="text-gray-500">—</div>
              ) : (
                <ul className="ml-5 list-disc text-gray-800">
                  {order.driver_names.map((n, i) => (
                    <li key={i}>
                      {n}
                      {order.driver_emails?.[i] ? (
                        <span className="ml-2 font-mono text-xs text-gray-500">
                          {order.driver_emails[i]}
                        </span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </Section>

            {order.internal_notes && (
              <Section title="Internal Notes">
                <div className="whitespace-pre-wrap rounded-md border border-gray-200 bg-[#f3f4f6] px-3 py-2 text-sm text-gray-800">
                  {order.internal_notes}
                </div>
              </Section>
            )}

            <Section title="Audit">
              <Row label="Created" value={fmt(order.created_at)} />
              <Row label="Created By" value={order.created_by || "—"} />
              <Row label="Last Modified" value={fmt(order.last_modified_at)} />
              <Row label="Last Modified By" value={order.last_modified_by || "—"} />
              <Row
                label="Email Sent"
                value={order.email_sent_at ? fmt(order.email_sent_at) : "Not sent"}
              />
              {order.last_send_error && (
                <div className="mt-2 rounded-md border border-red-200 bg-red-50/60 px-3 py-2 text-xs text-red-700">
                  <span className="font-semibold">Send error:</span> {order.last_send_error}
                </div>
              )}
            </Section>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <button
            data-testid="order-view-close"
            onClick={onClose}
            className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DispatchTimeline({ events, loading, order }) {
  if (loading) {
    return (
      <div className="rounded-md border border-gray-200 bg-[#f9fafb] p-4 text-center text-sm text-gray-500">
        <Spinner size={14} className="mr-2 inline animate-spin" /> Loading timeline…
      </div>
    );
  }
  if (events.length === 0) {
    return (
      <div className="rounded-md border border-gray-200 bg-[#f9fafb] p-4 text-center text-sm text-gray-500">
        No events recorded yet.
      </div>
    );
  }
  return (
    <div
      data-testid="dispatch-timeline"
      className="rounded-md border border-gray-200 bg-[#f9fafb] p-4"
    >
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs font-bold uppercase tracking-wider text-gray-500">
          Dispatch Timeline
        </div>
        <div className="text-xs text-gray-400">{events.length} events</div>
      </div>
      <ol className="space-y-2">
        {events.map((e) => (
          <li
            key={e.id}
            data-testid={`timeline-event-${e.type}`}
            className="flex items-start gap-3 text-sm"
          >
            <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-gray-200">
              <EventIcon type={e.type} />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="font-mono text-xs text-gray-500">{fmtTimeOnly(e.at)}</span>
                <span className="font-semibold text-gray-900">{e.label}</span>
                <span className="text-xs text-gray-500">by {e.by}</span>
              </div>
              <div className="mt-0.5 text-sm text-gray-700">{e.summary}</div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function EventIcon({ type }) {
  const map = {
    "order.created": { Icon: Plus, cls: "text-blue-600" },
    "order.updated": { Icon: PencilSimple, cls: "text-amber-600" },
    "order.cancelled": { Icon: XCircle, cls: "text-[#EB1D25]" },
    "driver.assigned": { Icon: UserPlus, cls: "text-green-600" },
    "driver.removed": { Icon: UserMinus, cls: "text-gray-600" },
    "priority.changed": { Icon: ArrowRight, cls: "text-amber-600" },
    "delivery_notes.changed": { Icon: PencilSimple, cls: "text-amber-600" },
    "internal_notes.changed": { Icon: PencilSimple, cls: "text-gray-600" },
    "products.changed": { Icon: PencilSimple, cls: "text-amber-600" },
    "address.changed": { Icon: PencilSimple, cls: "text-amber-600" },
    "delivery.completed": { Icon: Truck, cls: "text-green-600" },
    "notification.sent": { Icon: CheckCircle, cls: "text-green-600" },
    "notification.failed": { Icon: WarningCircle, cls: "text-[#EB1D25]" },
    "notification.retried": { Icon: PaperPlaneTilt, cls: "text-blue-600" },
  };
  const entry = map[type] || { Icon: PencilSimple, cls: "text-gray-500" };
  const { Icon, cls } = entry;
  return <Icon size={13} weight="bold" className={cls} />;
}

function Action({
  testid, icon: Icon, label, onClick, disabled, loading, active, tone = "default", hint,
}) {
  const toneCls =
    tone === "danger"
      ? "text-[#EB1D25] hover:bg-red-50"
      : tone === "primary"
        ? "text-white bg-[#EB1D25] hover:bg-[#AF101B]"
        : active
          ? "bg-gray-100 text-gray-900"
          : "text-gray-700 hover:bg-gray-100";
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={disabled || loading}
      title={hint || ""}
      className={`relative inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${toneCls}`}
    >
      {loading ? <Spinner size={13} className="animate-spin" /> : <Icon size={14} weight="bold" />}
      {label}
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="mb-1.5 text-xs font-bold uppercase tracking-wider text-gray-500">
        {title}
      </h3>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Row({ label, value, mono, bold, children }) {
  return (
    <div className="flex gap-3 text-sm">
      <span className="w-44 shrink-0 text-gray-500">{label}</span>
      <span
        className={`min-w-0 flex-1 text-gray-900 ${mono ? "font-mono" : ""} ${
          bold ? "font-semibold" : ""
        }`}
      >
        {children ?? value}
      </span>
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    Draft: "bg-gray-100 text-gray-700 border-gray-300",
    Sent: "bg-blue-50 text-blue-800 border-blue-300",
    Cancelled: "bg-red-50 text-red-800 border-red-300",
  };
  return (
    <span
      className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium ${
        styles[status] || styles.Sent
      }`}
    >
      {status}
    </span>
  );
}

function NotificationBadge({ status }) {
  if (status === "Sent") {
    return (
      <span className="inline-flex items-center rounded-sm border border-green-300 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-800">
        Notified
      </span>
    );
  }
  if (status === "Failed") {
    return (
      <span className="inline-flex items-center rounded-sm border border-red-300 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-800">
        Notification Failed
      </span>
    );
  }
  return null;
}

function renderPrintHtml(order) {
  const esc = (s) =>
    String(s || "").replace(/[&<>"]/g, (c) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;",
    })[c]);
  const products = (order.products || [])
    .map((p) => `<li>${esc(p.product_name)}</li>`)
    .join("");
  const drivers = (order.driver_names || [])
    .map(
      (n, i) =>
        `<li>${esc(n)}${order.driver_emails?.[i] ? ` <span style="color:#888">&lt;${esc(order.driver_emails[i])}&gt;</span>` : ""}</li>`,
    )
    .join("");
  return `<!doctype html><html><head><title>Order ${esc(order.order_number)}</title>
<style>
  body{font-family:-apple-system,Segoe UI,Arial,sans-serif;color:#111;max-width:640px;margin:24px auto;padding:0 20px;line-height:1.5;}
  h1{font-size:18px;margin:0 0 6px;color:#EB1D25;text-transform:uppercase;letter-spacing:1px;}
  h2{font-size:11px;margin:18px 0 6px;text-transform:uppercase;letter-spacing:1.5px;color:#555;border-bottom:1px solid #ddd;padding-bottom:3px;}
  .row{display:flex;gap:12px;padding:2px 0;font-size:13px;}
  .label{width:170px;color:#666;}
  ul{padding-left:18px;margin:4px 0;font-size:13px;}
  .meta{font-size:12px;color:#555;}
  @media print{ button{display:none;} }
</style></head><body>
<h1>Coolman Fuels — Fuel Delivery Order</h1>
<div class="meta">${esc(order.order_number)} · ${esc(order.customer_name)} · ${esc(order.status)}</div>
<h2>Customer</h2>
<div class="row"><span class="label">Name</span><span>${esc(order.customer_name)}</span></div>
<div class="row"><span class="label">Account #</span><span>${esc(order.account_number)}</span></div>
<div class="row"><span class="label">Mailing Address</span><span>${esc(order.mailing_address)}</span></div>
<div class="row"><span class="label">Phone</span><span>${esc(order.phone_number)}</span></div>
<h2>Delivery</h2>
<div class="row"><span class="label">Requested</span><span>${esc(order.priority)}</span></div>
${order.alternate_delivery_location ? `<div class="row"><span class="label">Alternate Location</span><span>${esc(order.alternate_delivery_location)}</span></div>` : ""}
${order.directions ? `<div class="row"><span class="label">Directions</span><span>${esc(order.directions)}</span></div>` : ""}
${order.delivery_notes ? `<div class="row"><span class="label">Delivery Notes</span><span>${esc(order.delivery_notes)}</span></div>` : ""}
<h2>Products</h2><ul>${products}</ul>
<h2>Drivers Notified</h2><ul>${drivers || "<li>—</li>"}</ul>
<h2>Audit</h2>
<div class="row"><span class="label">Created</span><span>${esc(fmt(order.created_at))} by ${esc(order.created_by)}</span></div>
<div class="row"><span class="label">Last Modified</span><span>${esc(fmt(order.last_modified_at))} by ${esc(order.last_modified_by)}</span></div>
</body></html>`;
}
